import { useAuth, useAuthActions } from '@/stores/auth';
import { isFeatureEnabled } from '@/config/features';
import { HOME_MODULES, type HomeModule } from '@/config/home-modules';

/**
 * Resolve the home-dashboard modules the signed-in user can access.
 *
 * Mirrors the Sidebar's access rules so the two surfaces never disagree:
 *  1. The module's feature-flag section must be enabled in this build.
 *  2. VIS-132: a user whose only privileged role is LEGAL_ADMIN sees Legal &
 *     Compliance only — never a Corporate Security surface.
 *  3. SUPER_ADMIN has access to every module.
 *  4. Otherwise the module id must be in the user's `moduleAccess`.
 *
 * Returns an empty array for an unauthenticated user or one with no module
 * access (the home page renders an explicit empty state for that case).
 */
export function useAccessibleModules(): HomeModule[] {
  const { user } = useAuth();
  const { checkModuleAccess } = useAuthActions();

  const roleNames = user?.roles.map((r) => r.name) ?? [];
  const isLegalAdminOnly =
    roleNames.includes('LEGAL_ADMIN') &&
    !roleNames.includes('SUPER_ADMIN') &&
    !roleNames.includes('ADMIN');
  const isSuperAdmin = roleNames.includes('SUPER_ADMIN');

  if (!user) {
    return [];
  }

  return HOME_MODULES.filter((module) => {
    if (!isFeatureEnabled(module.sectionId)) {
      return false;
    }
    if (isLegalAdminOnly) {
      return module.sectionId === 'legal';
    }
    if (isSuperAdmin) {
      return true;
    }
    return checkModuleAccess(module.moduleId);
  });
}
